import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  EyeIcon,
  DocumentTextIcon,
  TableCellsIcon,
  TrashIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '@/lib/auth';
import { projectsApi, schemaApi, migrationsApi } from '@/lib/api';
import toast from 'react-hot-toast';

interface TableColumn {
  column_name: string;
  data_type: string;
  is_nullable: boolean;
  column_default: string | null;
  is_primary_key: boolean;
  is_unique: boolean;
  character_maximum_length: number | null;
  numeric_precision?: number | null;
  numeric_scale?: number | null;
}

interface IndexInfo {
  index_name: string;
  is_unique: boolean;
  columns: string[];
  index_type: string;
}

interface ForeignKeyInfo {
  constraint_name: string;
  column_name: string;
  foreign_table_name: string;
  foreign_column_name: string;
  on_delete: string;
  on_update: string;
}

interface TriggerInfo {
  trigger_name: string;
  event_manipulation: string;
  action_timing: string;
  action_statement: string;
}

interface DatabaseTable {
  table_name: string;
  table_schema: string;
  table_type: string;
  is_insertable_into: boolean;
  columns: TableColumn[];
  indexes: IndexInfo[];
  foreign_keys: ForeignKeyInfo[];
  triggers: TriggerInfo[];
  row_count?: number;
}

interface DatabaseSchema {
  tables: DatabaseTable[];
  views: any[];
  functions: any[];
  sequences?: any[];
}

interface ColumnFormData {
  column_name: string;
  data_type: string;
  is_nullable: boolean;
  column_default: string;
  is_primary_key: boolean;
  is_unique: boolean;
  character_maximum_length: string;
}

interface TableFormData {
  table_name: string;
  columns: ColumnFormData[];
}

interface ColumnPatch {
  column_name: string;
  data_type: string;
  character_maximum_length: string;
  is_nullable: boolean;
  column_default: string;
  is_unique: boolean;
}

interface NewColumn extends ColumnPatch {
  id: string;
}

const COLUMN_TYPES = [
  'text',
  'varchar',
  'integer',
  'bigint',
  'uuid',
  'boolean',
  'timestamp with time zone',
  'date',
  'json',
  'jsonb',
  'numeric',
  'real',
  'double precision',
];

function quoteIdent(name: string): string {
  if (!/^[A-Za-z_][A-Za-z0-9_]*$/.test(name)) {
    throw new Error(`"${name}" is not a valid name`);
  }
  return `"${name}"`;
}

function baseType(dataType: string): string {
  const type = dataType.toLowerCase();
  if (type === 'character varying') return 'varchar';
  if (type === 'character') return 'char';
  return dataType;
}

function toPatch(column: TableColumn): ColumnPatch {
  return {
    column_name: column.column_name,
    data_type: baseType(column.data_type),
    character_maximum_length: column.character_maximum_length ? String(column.character_maximum_length) : '',
    is_nullable: column.is_nullable,
    column_default: column.column_default || '',
    is_unique: column.is_unique,
  };
}

function samePatch(left: ColumnPatch, right: ColumnPatch): boolean {
  return left.column_name === right.column_name
    && left.data_type === right.data_type
    && left.character_maximum_length === right.character_maximum_length
    && left.is_nullable === right.is_nullable
    && left.column_default === right.column_default
    && left.is_unique === right.is_unique;
}

function sqlType(patch: ColumnPatch): string {
  if ((patch.data_type === 'varchar' || patch.data_type === 'char') && patch.character_maximum_length) {
    return `${patch.data_type}(${patch.character_maximum_length})`;
  }
  return patch.data_type;
}

function fallbackDefault(patch: ColumnPatch): string {
  switch (patch.data_type) {
    case 'integer':
    case 'bigint':
    case 'numeric':
    case 'real':
    case 'double precision':
      return '0';
    case 'boolean':
      return 'false';
    case 'uuid':
      return 'gen_random_uuid()';
    case 'date':
      return 'CURRENT_DATE';
    case 'timestamp with time zone':
      return 'now()';
    case 'json':
    case 'jsonb':
      return "'{}'";
    default:
      return "''";
  }
}

function buildColumnAlterSql(
  tableName: string,
  original: TableColumn[],
  drafts: Record<string, ColumnPatch>,
  dropped: string[],
  added: NewColumn[]
): string {
  const table = quoteIdent(tableName);
  const droppedSet = new Set(dropped);
  const statements: string[] = [];

  for (const column of original) {
    if (droppedSet.has(column.column_name)) {
      statements.push(`ALTER TABLE ${table} DROP COLUMN ${quoteIdent(column.column_name)}`);
      continue;
    }
    const draft = drafts[column.column_name];
    const before = toPatch(column);
    if (!draft || samePatch(before, draft)) continue;

    let currentName = quoteIdent(column.column_name);
    if (draft.column_name !== column.column_name) {
      const renamed = quoteIdent(draft.column_name);
      statements.push(`ALTER TABLE ${table} RENAME COLUMN ${currentName} TO ${renamed}`);
      currentName = renamed;
    }
    if (sqlType(before) !== sqlType(draft)) {
      const nextType = sqlType(draft);
      statements.push(`ALTER TABLE ${table} ALTER COLUMN ${currentName} TYPE ${nextType} USING CAST(${currentName} AS ${nextType})`);
    }
    if (before.is_nullable !== draft.is_nullable) {
      statements.push(`ALTER TABLE ${table} ALTER COLUMN ${currentName} ${draft.is_nullable ? 'DROP' : 'SET'} NOT NULL`);
    }
    if (before.column_default !== draft.column_default) {
      statements.push(
        draft.column_default.trim()
          ? `ALTER TABLE ${table} ALTER COLUMN ${currentName} SET DEFAULT ${draft.column_default.trim()}`
          : `ALTER TABLE ${table} ALTER COLUMN ${currentName} DROP DEFAULT`
      );
    }
    if (before.is_unique !== draft.is_unique) {
      const constraint = quoteIdent(`${tableName}_${draft.column_name}_key`);
      statements.push(
        draft.is_unique
          ? `ALTER TABLE ${table} ADD CONSTRAINT ${constraint} UNIQUE (${currentName})`
          : `ALTER TABLE ${table} DROP CONSTRAINT IF EXISTS ${constraint}`
      );
    }
  }

  for (const column of added) {
    if (!column.column_name.trim()) continue;
    const name = quoteIdent(column.column_name);
    let definition = `${name} ${sqlType(column)}`;
    const hasDefault = Boolean(column.column_default.trim());
    if (!column.is_nullable && !hasDefault) {
      // Existing rows need a value, or Postgres rejects the new NOT NULL column.
      definition += ` NOT NULL DEFAULT ${fallbackDefault(column)}`;
      if (column.is_unique) definition += ' UNIQUE';
      statements.push(`ALTER TABLE ${table} ADD COLUMN ${definition}`);
      statements.push(`ALTER TABLE ${table} ALTER COLUMN ${name} DROP DEFAULT`);
      continue;
    }
    if (!column.is_nullable) definition += ' NOT NULL';
    if (hasDefault) definition += ` DEFAULT ${column.column_default.trim()}`;
    if (column.is_unique) definition += ' UNIQUE';
    statements.push(`ALTER TABLE ${table} ADD COLUMN ${definition}`);
  }

  return statements.join(';\n');
}

function CellInput({
  value,
  onCommit,
  onCancel,
}: {
  value: string;
  onCommit: (value: string) => void;
  onCancel: () => void;
}) {
  const [local, setLocal] = useState(value);
  const cancelled = useRef(false);
  const fieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fieldRef.current?.focus();
    fieldRef.current?.select();
  }, []);

  return (
    <input
      ref={fieldRef}
      value={local}
      onChange={(event) => setLocal(event.target.value)}
      onBlur={() => {
        if (!cancelled.current) onCommit(local);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          cancelled.current = true;
          onCommit(local);
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          cancelled.current = true;
          onCancel();
        }
      }}
      className="form-input py-1 text-sm"
    />
  );
}

export default function SchemaManagement() {
  const { id: projectId } = useParams<{ id: string }>();
  const { isAuthenticated, isLoading: authLoading } = useAuth();
  const queryClient = useQueryClient();
  const [selectedTable, setSelectedTable] = useState<DatabaseTable | null>(null);
  const [showSQLModal, setShowSQLModal] = useState(false);
  const [selectedTab, setSelectedTab] = useState<'tables' | 'views' | 'functions'>('tables');
  const [showCreateTableModal, setShowCreateTableModal] = useState(false);
  const [showEditTableModal, setShowEditTableModal] = useState(false);
  const [columnDrafts, setColumnDrafts] = useState<Record<string, ColumnPatch>>({});
  const [droppedColumns, setDroppedColumns] = useState<string[]>([]);
  const [addedColumns, setAddedColumns] = useState<NewColumn[]>([]);
  const [editingCell, setEditingCell] = useState<{ key: string; field: string } | null>(null);
  const [tableForm, setTableForm] = useState<TableFormData>({
    table_name: '',
    columns: [
      {
        column_name: 'id',
        data_type: 'uuid',
        is_nullable: false,
        column_default: 'gen_random_uuid()',
        is_primary_key: true,
        is_unique: false,
        character_maximum_length: '',
      },
      {
        column_name: 'created_at',
        data_type: 'timestamp with time zone',
        is_nullable: false,
        column_default: 'now()',
        is_primary_key: false,
        is_unique: false,
        character_maximum_length: '',
      },
    ],
  });

  // Fetch project details
  const { data: projectResponse } = useQuery(
    ['project', projectId],
    () => projectsApi.get(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
    }
  );

  // Fetch schema for selected project
  const { data: schemaResponse, isLoading: schemaLoading } = useQuery(
    ['schema', projectId],
    () => schemaApi.getSchema(projectId as string),
    {
      enabled: isAuthenticated && !!projectId,
    }
  );

  // Fetch schema as SQL
  const { data: sqlResponse } = useQuery(
    ['schema-sql', projectId],
    () => schemaApi.getSchemaSQL(projectId as string),
    {
      enabled: isAuthenticated && !!projectId && showSQLModal,
    }
  );

  // Validate SQL mutation
  const validateSQLMutation = useMutation(
    (sql: string) =>
      schemaApi.validateSQL(projectId as string, sql),
    {
      onSuccess: (data) => {
        if (data.success && data.data.valid) {
          toast.success('SQL is valid');
        } else {
          toast.error(`SQL validation failed: ${data.data.errors?.join(', ')}`);
        }
      },
      onError: () => {
        toast.error('Failed to validate SQL');
      },
    }
  );

  // Create table mutation
  const createTableMutation = useMutation(
    async (sql: string) => {
      // First create a migration
      const migrationResponse = await migrationsApi.createMigration(projectId as string, {
        name: `create_table_${tableForm.table_name}`,
        up_sql: sql,
        down_sql: `DROP TABLE IF EXISTS ${tableForm.table_name} CASCADE;`,
      });
      
      // Then execute it immediately
      const migrationId = migrationResponse.data?.id || migrationResponse.id;
      if (!migrationId) {
        throw new Error('Migration ID not found in response');
      }
      
      await migrationsApi.applyMigration(migrationId);
      
      return migrationResponse;
    },
    {
      onSuccess: () => {
        toast.success('Table created successfully');
        setShowCreateTableModal(false);
        setTableForm({
          table_name: '',
          columns: [
            {
              column_name: 'id',
              data_type: 'uuid',
              is_nullable: false,
              column_default: 'gen_random_uuid()',
              is_primary_key: true,
              is_unique: false,
              character_maximum_length: '',
            },
            {
              column_name: 'created_at',
              data_type: 'timestamp with time zone',
              is_nullable: false,
              column_default: 'now()',
              is_primary_key: false,
              is_unique: false,
              character_maximum_length: '',
            },
          ],
        });
        queryClient.invalidateQueries(['schema', projectId]);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to create table';
        toast.error(errorMessage);
      },
    }
  );

  const saveColumnsMutation = useMutation(
    async (sql: string) => {
      if (!selectedTable) throw new Error('Select a table first');
      const migrationResponse = await migrationsApi.createMigration(projectId as string, {
        name: `alter_table_${selectedTable.table_name}`,
        description: 'Column edits from the schema page',
        up_sql: sql,
        down_sql: '-- Manual rollback required',
      });
      const migrationId = migrationResponse.data?.id || migrationResponse.id;
      if (!migrationId) throw new Error('Migration ID not found in response');
      await migrationsApi.applyMigration(migrationId);
      return migrationResponse;
    },
    {
      onSuccess: () => {
        toast.success('Columns saved');
        setColumnDrafts({});
        setDroppedColumns([]);
        setAddedColumns([]);
        setEditingCell(null);
        queryClient.invalidateQueries(['schema', projectId]);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to update columns';
        toast.error(errorMessage);
      },
    }
  );

  // Drop table mutation
  const dropTableMutation = useMutation(
    async (tableName: string) => {
      const sql = `DROP TABLE IF EXISTS ${tableName} CASCADE;`;
      
      // Create and execute migration for dropping table
      const migrationResponse = await migrationsApi.createMigration(projectId as string, {
        name: `drop_table_${tableName}`,
        up_sql: sql,
        down_sql: '', // Can't easily recreate without storing original schema
      });
      
      const migrationId = migrationResponse.data?.id || migrationResponse.id;
      if (!migrationId) {
        throw new Error('Migration ID not found in response');
      }
      
      await migrationsApi.applyMigration(migrationId);
      
      return migrationResponse;
    },
    {
      onSuccess: () => {
        toast.success('Table dropped successfully');
        setSelectedTable(null);
        queryClient.invalidateQueries(['schema', projectId]);
      },
      onError: (error: any) => {
        const errorMessage = error?.response?.data?.message || error?.message || 'Failed to drop table';
        toast.error(errorMessage);
      },
    }
  );

  const handleAddColumn = () => {
    setTableForm({
      ...tableForm,
      columns: [
        ...tableForm.columns,
        {
          column_name: '',
          data_type: 'text',
          is_nullable: true,
          column_default: '',
          is_primary_key: false,
          is_unique: false,
          character_maximum_length: '',
        },
      ],
    });
  };

  const handleRemoveColumn = (index: number) => {
    setTableForm({
      ...tableForm,
      columns: tableForm.columns.filter((_, i) => i !== index),
    });
  };

  const handleColumnChange = (index: number, field: keyof ColumnFormData, value: any) => {
    const newColumns = [...tableForm.columns];
    newColumns[index] = { ...newColumns[index], [field]: value };
    setTableForm({ ...tableForm, columns: newColumns });
  };

  const generateCreateTableSQL = () => {
    const columnDefs = tableForm.columns.map((col) => {
      let def = `  ${col.column_name} ${col.data_type}`;
      
      if (col.character_maximum_length && (col.data_type.includes('varchar') || col.data_type.includes('char'))) {
        def += `(${col.character_maximum_length})`;
      }
      
      if (col.is_primary_key) {
        def += ' PRIMARY KEY';
      }
      
      if (!col.is_nullable) {
        def += ' NOT NULL';
      }
      
      if (col.is_unique && !col.is_primary_key) {
        def += ' UNIQUE';
      }
      
      if (col.column_default) {
        def += ` DEFAULT ${col.column_default}`;
      }
      
      return def;
    });

    return `CREATE TABLE ${tableForm.table_name} (\n${columnDefs.join(',\n')}\n);`;
  };

  const handleCreateTable = () => {
    if (!tableForm.table_name) {
      toast.error('Table name is required');
      return;
    }

    if (tableForm.columns.length === 0) {
      toast.error('At least one column is required');
      return;
    }

    const hasEmptyColumnNames = tableForm.columns.some((col) => !col.column_name);
    if (hasEmptyColumnNames) {
      toast.error('All columns must have a name');
      return;
    }

    const sql = generateCreateTableSQL();
    createTableMutation.mutate(sql);
  };

  const handleDropTable = (tableName: string) => {
    if (confirm(`Are you sure you want to drop the table "${tableName}"? This action cannot be undone.`)) {
      dropTableMutation.mutate(tableName);
    }
  };

  const clearColumnEdits = () => {
    setColumnDrafts({});
    setDroppedColumns([]);
    setAddedColumns([]);
    setEditingCell(null);
  };

  const commitColumn = (originalName: string, patch: Partial<ColumnPatch>, close = true) => {
    const column = selectedTable?.columns.find((item) => item.column_name === originalName);
    if (!column) return;
    setColumnDrafts((current) => {
      const next = { ...(current[originalName] ?? toPatch(column)), ...patch };
      const updated = { ...current };
      if (samePatch(toPatch(column), next)) delete updated[originalName];
      else updated[originalName] = next;
      return updated;
    });
    if (close) setEditingCell(null);
  };

  const saveColumnEdits = () => {
    if (!selectedTable) return;
    const pendingAdded = addedColumns.filter((column) => column.column_name.trim());
    if (addedColumns.some((column) => !column.column_name.trim())) {
      toast.error('New columns need a name');
      return;
    }
    let sql = '';
    try {
      sql = buildColumnAlterSql(selectedTable.table_name, selectedTable.columns, columnDrafts, droppedColumns, pendingAdded);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not build the column change');
      return;
    }
    if (!sql.trim()) {
      toast.error('No column changes to save');
      return;
    }
    saveColumnsMutation.mutate(sql);
  };

  useEffect(() => {
    const nextSchema = schemaResponse?.data?.schema || schemaResponse?.data;
    const tables: DatabaseTable[] = nextSchema?.tables || [];
    setSelectedTable((current) => {
      if (!current) return current;
      return tables.find((table) => table.table_name === current.table_name) ?? current;
    });
  }, [schemaResponse]);

  if (authLoading || !isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const project = projectResponse?.data?.project;

  // Debug: Log the actual response structure
  if (schemaResponse && typeof window !== 'undefined') {
    console.log('Schema response:', JSON.stringify(schemaResponse, null, 2));
  }

  // Handle both nested and flat response structures
  const schema: DatabaseSchema = schemaResponse?.data?.schema || schemaResponse?.data || { tables: [], views: [], functions: [] };

  const getDataTypeColor = (dataType: string) => {
    const type = dataType.toLowerCase();
    if (type.includes('varchar') || type.includes('text') || type.includes('char')) {
      return 'bg-blue-100 text-blue-800';
    }
    if (type.includes('int') || type.includes('numeric') || type.includes('decimal')) {
      return 'bg-green-100 text-green-800';
    }
    if (type.includes('boolean') || type.includes('bool')) {
      return 'bg-purple-100 text-purple-800';
    }
    if (type.includes('timestamp') || type.includes('date') || type.includes('time')) {
      return 'bg-yellow-100 text-yellow-800';
    }
    if (type.includes('uuid')) {
      return 'bg-pink-100 text-pink-800';
    }
    return 'bg-gray-100 text-gray-800';
  };

  const tabs = [
    { id: 'tables', name: 'Tables', icon: TableCellsIcon },
    { id: 'views', name: 'Views', icon: EyeIcon },
    { id: 'functions', name: 'Functions', icon: DocumentTextIcon },
  ];

  return (
      <div className="px-4 sm:px-0">
        <div className="sm:flex sm:items-center">
          <div className="sm:flex-auto">
            <h1 className="text-2xl font-semibold leading-6 text-gray-900">
              Schema Management
            </h1>
            <p className="mt-2 text-sm text-gray-700">
              Explore and manage the database schema for {project?.name || 'this project'}.
            </p>
          </div>
          {projectId && (
            <div className="mt-4 sm:mt-0 sm:ml-16 sm:flex-none space-x-2">
              <button
                onClick={() => setShowCreateTableModal(true)}
                className="btn-primary"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                New Table
              </button>
              <button
                onClick={() => setShowSQLModal(true)}
                className="btn-secondary"
              >
                <DocumentTextIcon className="h-4 w-4 mr-2" />
                View SQL
              </button>
            </div>
          )}
        </div>

        {project && (
          <>
            {/* Tab Navigation */}
            <div className="mt-6 border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setSelectedTab(tab.id as any)}
                    className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center ${
                      selectedTab === tab.id
                        ? 'border-primary-500 text-primary-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <tab.icon className="h-4 w-4 mr-2" />
                    {tab.name} ({tab.id === 'tables' ? (schema.tables?.length || 0) : 
                              tab.id === 'views' ? (schema.views?.length || 0) : 
                              (schema.functions?.length || 0)})
                  </button>
                ))}
              </nav>
            </div>

            {schemaLoading ? (
              <div className="mt-6 animate-pulse space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-20 bg-gray-200 rounded"></div>
                ))}
              </div>
            ) : (
              <div className="mt-6">
                {/* Tables Tab */}
                {selectedTab === 'tables' && (
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Tables List */}
                    <div className="lg:col-span-1">
                      <div className="card">
                        <h2 className="text-lg font-medium text-gray-900 mb-4">Database Tables</h2>
                        
                        {(schema.tables?.length || 0) === 0 ? (
                          <div className="text-center py-8">
                            <TableCellsIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">No tables found</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              This database doesn&apos;t have any tables yet.
                            </p>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {(schema.tables || []).map((table) => (
                              <button
                                key={table.table_name}
                                onClick={() => {
                                  setSelectedTable(table);
                                  clearColumnEdits();
                                }}
                                className={`w-full text-left p-3 rounded-lg border transition-colors ${
                                  selectedTable?.table_name === table.table_name
                                    ? 'border-primary-200 bg-primary-50'
                                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }`}
                              >
                                <div className="flex items-center justify-between">
                                  <span className="font-medium text-gray-900">
                                    {table.table_name}
                                  </span>
                                  <span className="text-xs text-gray-500">
                                    {table.columns.length} columns
                                  </span>
                                </div>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Table Details */}
                    <div className="lg:col-span-2">
                      {selectedTable ? (
                        <div className="card">
                          <div className="flex items-center justify-between mb-4">
                            <h2 className="text-lg font-medium text-gray-900">
                              {selectedTable.table_name}
                            </h2>
                            <div className="flex space-x-2">
                              <button
                                onClick={() => setAddedColumns((current) => [...current, {
                                  id: crypto.randomUUID(),
                                  column_name: '',
                                  data_type: 'text',
                                  character_maximum_length: '',
                                  is_nullable: true,
                                  column_default: '',
                                  is_unique: false,
                                }])}
                                className="btn-secondary"
                              >
                                <PlusIcon className="h-4 w-4 mr-2" />
                                Column
                              </button>
                              <button 
                                onClick={() => handleDropTable(selectedTable.table_name)}
                                className="btn-danger"
                              >
                                <TrashIcon className="h-4 w-4 mr-2" />
                                Drop
                              </button>
                            </div>
                          </div>

                          <p className="mb-3 text-xs text-gray-500">Double-click a cell to edit that column. Changed rows are saved together.</p>
                          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
                            <table className="min-w-full divide-y divide-gray-300">
                              <thead className="bg-gray-50">
                                <tr>
                                  {['Column', 'Type', 'Nullable', 'Default', 'Unique', ''].map((heading) => (
                                    <th key={heading} className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wide">
                                      {heading}
                                    </th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody className="bg-white divide-y divide-gray-200">
                                {selectedTable.columns.map((column) => {
                                  const dropped = droppedColumns.includes(column.column_name);
                                  const patch = columnDrafts[column.column_name] ?? toPatch(column);
                                  const dirty = dropped || !samePatch(toPatch(column), patch);
                                  const typeLabel = sqlType(patch);
                                  return (
                                    <tr key={column.column_name} className={dropped ? 'bg-red-50 line-through' : dirty ? 'bg-amber-50' : undefined}>
                                      <td className="px-3 py-3" onDoubleClick={() => setEditingCell({ key: column.column_name, field: 'name' })}>
                                        {editingCell?.key === column.column_name && editingCell.field === 'name' && !dropped ? (
                                          <CellInput
                                            value={patch.column_name}
                                            onCommit={(value) => commitColumn(column.column_name, { column_name: value.trim() })}
                                            onCancel={() => setEditingCell(null)}
                                          />
                                        ) : (
                                          <span className="text-sm font-medium text-gray-900">
                                            {patch.column_name}
                                            {column.is_primary_key && <span className="ml-2 rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-medium text-yellow-800 no-underline">PK</span>}
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-3" onDoubleClick={() => setEditingCell({ key: column.column_name, field: 'type' })}>
                                        {editingCell?.key === column.column_name && editingCell.field === 'type' && !dropped ? (
                                          <div className="flex gap-2" onBlur={(event) => {
                                            if (!event.currentTarget.contains(event.relatedTarget as Node)) setEditingCell(null);
                                          }}>
                                            <select
                                              autoFocus
                                              value={patch.data_type}
                                              onChange={(event) => commitColumn(column.column_name, { data_type: event.target.value }, false)}
                                              className="form-select py-1 text-sm"
                                            >
                                              {!COLUMN_TYPES.includes(patch.data_type) && <option value={patch.data_type}>{patch.data_type}</option>}
                                              {COLUMN_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                                            </select>
                                            {(patch.data_type === 'varchar' || patch.data_type === 'char') && (
                                              <input
                                                type="number"
                                                value={patch.character_maximum_length}
                                                onChange={(event) => commitColumn(column.column_name, { character_maximum_length: event.target.value }, false)}
                                                className="form-input w-20 py-1 text-sm"
                                                placeholder="length"
                                              />
                                            )}
                                          </div>
                                        ) : (
                                          <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-medium ${getDataTypeColor(typeLabel)}`}>{typeLabel}</span>
                                        )}
                                      </td>
                                      <td className="px-3 py-3 text-sm" onDoubleClick={() => setEditingCell({ key: column.column_name, field: 'nullable' })}>
                                        {editingCell?.key === column.column_name && editingCell.field === 'nullable' && !dropped ? (
                                          <select
                                            autoFocus
                                            value={patch.is_nullable ? 'yes' : 'no'}
                                            onChange={(event) => commitColumn(column.column_name, { is_nullable: event.target.value === 'yes' })}
                                            className="form-select py-1 text-sm"
                                          >
                                            <option value="yes">Yes</option>
                                            <option value="no">No</option>
                                          </select>
                                        ) : (
                                          <span className={patch.is_nullable ? 'text-green-600' : 'text-red-600'}>{patch.is_nullable ? 'Yes' : 'No'}</span>
                                        )}
                                      </td>
                                      <td className="px-3 py-3 font-mono text-sm" onDoubleClick={() => setEditingCell({ key: column.column_name, field: 'default' })}>
                                        {editingCell?.key === column.column_name && editingCell.field === 'default' && !dropped ? (
                                          <CellInput
                                            value={patch.column_default}
                                            onCommit={(value) => commitColumn(column.column_name, { column_default: value })}
                                            onCancel={() => setEditingCell(null)}
                                          />
                                        ) : (
                                          patch.column_default || <span className="text-gray-400">—</span>
                                        )}
                                      </td>
                                      <td className="px-3 py-3" onDoubleClick={() => commitColumn(column.column_name, { is_unique: !patch.is_unique })}>
                                        <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${patch.is_unique ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}>
                                          {patch.is_unique ? 'Unique' : 'Not unique'}
                                        </span>
                                      </td>
                                      <td className="px-3 py-3 text-right">
                                        <button
                                          type="button"
                                          onClick={() => setDroppedColumns((current) => current.includes(column.column_name)
                                            ? current.filter((name) => name !== column.column_name)
                                            : [...current, column.column_name])}
                                          className="text-gray-400 hover:text-red-600"
                                          aria-label={dropped ? 'Keep column' : 'Drop column'}
                                        >
                                          <TrashIcon className="h-4 w-4" />
                                        </button>
                                      </td>
                                    </tr>
                                  );
                                })}
                                {addedColumns.map((column) => (
                                  <tr key={column.id} className="bg-amber-50">
                                    <td className="px-3 py-3">
                                      <CellInput
                                        value={column.column_name}
                                        onCommit={(value) => setAddedColumns((current) => current.map((item) => item.id === column.id ? { ...item, column_name: value.trim() } : item))}
                                        onCancel={() => undefined}
                                      />
                                    </td>
                                    <td className="px-3 py-3">
                                      <select
                                        value={column.data_type}
                                        onChange={(event) => setAddedColumns((current) => current.map((item) => item.id === column.id ? { ...item, data_type: event.target.value } : item))}
                                        className="form-select py-1 text-sm"
                                      >
                                        {COLUMN_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}
                                      </select>
                                    </td>
                                    <td className="px-3 py-3">
                                      <select
                                        value={column.is_nullable ? 'yes' : 'no'}
                                        onChange={(event) => setAddedColumns((current) => current.map((item) => item.id === column.id ? { ...item, is_nullable: event.target.value === 'yes' } : item))}
                                        className="form-select py-1 text-sm"
                                      >
                                        <option value="yes">Yes</option>
                                        <option value="no">No</option>
                                      </select>
                                    </td>
                                    <td className="px-3 py-3">
                                      <CellInput
                                        value={column.column_default}
                                        onCommit={(value) => setAddedColumns((current) => current.map((item) => item.id === column.id ? { ...item, column_default: value } : item))}
                                        onCancel={() => undefined}
                                      />
                                    </td>
                                    <td className="px-3 py-3">
                                      <button
                                        type="button"
                                        onClick={() => setAddedColumns((current) => current.map((item) => item.id === column.id ? { ...item, is_unique: !item.is_unique } : item))}
                                        className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${column.is_unique ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-500'}`}
                                      >
                                        {column.is_unique ? 'Unique' : 'Not unique'}
                                      </button>
                                    </td>
                                    <td className="px-3 py-3 text-right">
                                      <button
                                        type="button"
                                        onClick={() => setAddedColumns((current) => current.filter((item) => item.id !== column.id))}
                                        className="text-gray-400 hover:text-red-600"
                                        aria-label="Remove new column"
                                      >
                                        <TrashIcon className="h-4 w-4" />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      ) : (
                        <div className="card">
                          <div className="text-center py-12">
                            <TableCellsIcon className="mx-auto h-12 w-12 text-gray-400" />
                            <h3 className="mt-2 text-sm font-medium text-gray-900">Select a table</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Choose a table from the list to view its structure and details.
                            </p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Views Tab */}
                {selectedTab === 'views' && (
                  <div className="card">
                    <div className="text-center py-12">
                      <EyeIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">Views</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {(schema.views?.length || 0) === 0 
                          ? 'No views found in this database.'
                          : `Found ${schema.views?.length || 0} views in this database.`
                        }
                      </p>
                    </div>
                  </div>
                )}

                {/* Functions Tab */}
                {selectedTab === 'functions' && (
                  <div className="card">
                    <div className="text-center py-12">
                      <DocumentTextIcon className="mx-auto h-12 w-12 text-gray-400" />
                      <h3 className="mt-2 text-sm font-medium text-gray-900">Functions</h3>
                      <p className="mt-1 text-sm text-gray-500">
                        {(schema.functions?.length || 0) === 0 
                          ? 'No functions found in this database.'
                          : `Found ${schema.functions?.length || 0} functions in this database.`
                        }
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {(Object.keys(columnDrafts).length > 0 || droppedColumns.length > 0 || addedColumns.length > 0) && (
          <button
            type="button"
            onClick={saveColumnEdits}
            disabled={saveColumnsMutation.isLoading}
            className="btn-primary fixed bottom-6 right-8 z-30 shadow-lg disabled:cursor-not-allowed disabled:opacity-50"
          >
            {saveColumnsMutation.isLoading ? 'Saving…' : 'Save columns'}
          </button>
        )}

        {/* SQL Modal */}
        {showSQLModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Schema SQL</h3>
                  <button
                    onClick={() => setShowSQLModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <span className="sr-only">Close</span>
                    ✕
                  </button>
                </div>
                
                <div className="bg-gray-50 rounded-lg p-4 max-h-96 overflow-auto">
                  <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                    {sqlResponse || 'Loading schema SQL...'}
                  </pre>
                </div>

                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    onClick={() => {
                      if (sqlResponse) {
                        navigator.clipboard.writeText(sqlResponse);
                        toast.success('SQL copied to clipboard');
                      }
                    }}
                    className="btn-secondary"
                  >
                    Copy SQL
                  </button>
                  <button
                    onClick={() => {
                      if (sqlResponse) {
                        validateSQLMutation.mutate(sqlResponse);
                      }
                    }}
                    disabled={validateSQLMutation.isLoading}
                    className="btn-primary disabled:opacity-50"
                  >
                    {validateSQLMutation.isLoading ? 'Validating...' : 'Validate SQL'}
                  </button>
                  <button
                    onClick={() => setShowSQLModal(false)}
                    className="btn-secondary"
                  >
                    Close
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Create/Edit Table Modal */}
        {(showCreateTableModal || showEditTableModal) && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-4xl shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900">
                    {showEditTableModal ? 'Edit Table' : 'Create New Table'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowCreateTableModal(false);
                      setShowEditTableModal(false);
                    }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Table Name */}
                  <div>
                    <label className="form-label">Table Name</label>
                    <input
                      type="text"
                      value={tableForm.table_name}
                      onChange={(e) => setTableForm({ ...tableForm, table_name: e.target.value })}
                      className="form-input"
                      placeholder="users, products, etc."
                      disabled={showEditTableModal}
                    />
                  </div>

                  {/* Columns */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="form-label mb-0">Columns</label>
                      <button
                        onClick={handleAddColumn}
                        className="btn-secondary text-sm"
                      >
                        <PlusIcon className="h-4 w-4 mr-1" />
                        Add Column
                      </button>
                    </div>

                    <div className="space-y-3 max-h-96 overflow-y-auto">
                      {tableForm.columns.map((column, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start justify-between mb-3">
                            <h4 className="text-sm font-medium text-gray-900">
                              Column {index + 1}
                            </h4>
                            {tableForm.columns.length > 1 && (
                              <button
                                onClick={() => handleRemoveColumn(index)}
                                className="text-red-600 hover:text-red-800"
                              >
                                <TrashIcon className="h-4 w-4" />
                              </button>
                            )}
                          </div>

                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <label className="text-xs text-gray-600">Column Name</label>
                              <input
                                type="text"
                                value={column.column_name}
                                onChange={(e) => handleColumnChange(index, 'column_name', e.target.value)}
                                className="form-input text-sm"
                                placeholder="column_name"
                              />
                            </div>

                            <div>
                              <label className="text-xs text-gray-600">Data Type</label>
                              <select
                                value={column.data_type}
                                onChange={(e) => handleColumnChange(index, 'data_type', e.target.value)}
                                className="form-input text-sm"
                              >
                                <option value="text">text</option>
                                <option value="varchar">varchar</option>
                                <option value="integer">integer</option>
                                <option value="bigint">bigint</option>
                                <option value="uuid">uuid</option>
                                <option value="boolean">boolean</option>
                                <option value="timestamp with time zone">timestamp with time zone</option>
                                <option value="date">date</option>
                                <option value="json">json</option>
                                <option value="jsonb">jsonb</option>
                                <option value="numeric">numeric</option>
                                <option value="real">real</option>
                                <option value="double precision">double precision</option>
                              </select>
                            </div>

                            {(column.data_type.includes('varchar') || column.data_type.includes('char')) && (
                              <div>
                                <label className="text-xs text-gray-600">Max Length</label>
                                <input
                                  type="number"
                                  value={column.character_maximum_length}
                                  onChange={(e) => handleColumnChange(index, 'character_maximum_length', e.target.value)}
                                  className="form-input text-sm"
                                  placeholder="255"
                                />
                              </div>
                            )}

                            <div>
                              <label className="text-xs text-gray-600">Default Value</label>
                              <input
                                type="text"
                                value={column.column_default}
                                onChange={(e) => handleColumnChange(index, 'column_default', e.target.value)}
                                className="form-input text-sm"
                                placeholder="null, now(), etc."
                              />
                            </div>

                            <div className="col-span-2 flex space-x-4">
                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={column.is_primary_key}
                                  onChange={(e) => handleColumnChange(index, 'is_primary_key', e.target.checked)}
                                  className="mr-2"
                                />
                                <span className="text-xs text-gray-600">Primary Key</span>
                              </label>

                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={!column.is_nullable}
                                  onChange={(e) => handleColumnChange(index, 'is_nullable', !e.target.checked)}
                                  className="mr-2"
                                />
                                <span className="text-xs text-gray-600">Required (NOT NULL)</span>
                              </label>

                              <label className="flex items-center">
                                <input
                                  type="checkbox"
                                  checked={column.is_unique}
                                  onChange={(e) => handleColumnChange(index, 'is_unique', e.target.checked)}
                                  className="mr-2"
                                />
                                <span className="text-xs text-gray-600">Unique</span>
                              </label>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Preview SQL */}
                  {!showEditTableModal && (
                    <div>
                      <label className="form-label">SQL Preview</label>
                      <div className="bg-gray-50 rounded-lg p-4">
                        <pre className="text-sm text-gray-700 whitespace-pre-wrap">
                          {generateCreateTableSQL()}
                        </pre>
                      </div>
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-4 mt-6">
                  <button
                    onClick={() => {
                      setShowCreateTableModal(false);
                      setShowEditTableModal(false);
                    }}
                    className="btn-secondary"
                  >
                    Cancel
                  </button>
                  {!showEditTableModal && (
                    <button
                      onClick={handleCreateTable}
                      disabled={createTableMutation.isLoading}
                      className="btn-primary disabled:opacity-50"
                    >
                      {createTableMutation.isLoading ? 'Creating...' : 'Create Table'}
                    </button>
                  )}

                </div>
              </div>
            </div>
          </div>
        )}
      </div>
  );
}